// import ArchiveIcon from "@mui/icons-material/Archive";
import ArticleIcon from "@mui/icons-material/Article";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import EmailIcon from "@mui/icons-material/Email";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Toolbar from "@mui/material/Toolbar";
import { CSSObject, Theme, styled, useTheme } from "@mui/material/styles";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppRoutes from "../../routes";
import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { useAppSelector } from "../store/store";
import { useMemo } from "react";
import NewspaperIcon from '@mui/icons-material/Newspaper';
import BadgeIcon from '@mui/icons-material/Badge';
import ContactPageIcon from "@mui/icons-material/ContactPage";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BusinessIcon from "@mui/icons-material/Business";
import WorkOffIcon from "@mui/icons-material/WorkOff";
import SendIcon from "@mui/icons-material/Send";
import ClassIcon from "@mui/icons-material/Class";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CalculateIcon from "@mui/icons-material/Calculate";
import jwt from "jsonwebtoken";
import { clearReturnUrl } from "../../modules/auth/return-url";
import { brand } from "../theme/app-theme";
import { isReadOnlyDirector } from "../roles";
import { ESOBES_ENABLED } from "../../modules/esobes/models";

type CurrentUserLabel = {
  primary: string;
  secondary?: string;
  rolesLabel?: string;
};

const ROLE_LABELS: Record<string, string> = {
  ROLE_SPECIALIST: "Специалист",
  ROLE_ACCOUNTANT: "Бухгалтер",
  ROLE_MAIN_ACCOUNTANT: "Главный бухгалтер",
  ROLE_DEPARTMENT_HEAD: "Руководитель отдела",
  ROLE_DEPUTY_DIRECTOR: "Зам. директора",
  ROLE_DIRECTOR: "Директор",
  ROLE_AUDIT: "Аудит",
  ROLE_SYSTEM: "Система",
  ROLE_INTEGRATION: "Интеграция",
};

const IGNORED_ROLES = new Set([
  "offline_access",
  "uma_authorization",
  "admin",
  "create-realm",
  "default-roles-master",
]);

function formatRoles(roles: string[] = []): string | undefined {
  const labels = roles
    .filter((role) => role && !IGNORED_ROLES.has(role))
    .map((role) => ROLE_LABELS[role] || role.replace(/^ROLE_/, ""))
    .filter(Boolean);
  return labels.length ? labels.join(", ") : undefined;
}

function getCurrentUserLabel(reduxRoles: string[] = []): CurrentUserLabel | null {
  const token = localStorage.getItem("access_token");
  const username = localStorage.getItem("username") || undefined;
  if (!token && !username) return null;

  let claims: Record<string, any> = {};
  if (token) {
    try {
      claims = (jwt.decode(token) as Record<string, any>) || {};
    } catch {
      claims = {};
    }
  }

  const fullName = [claims.family_name, claims.given_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const primary =
    (claims.name && String(claims.name).trim()) ||
    fullName ||
    claims.email ||
    claims.preferred_username ||
    username;

  if (!primary) return null;

  const secondary =
    claims.email && primary !== claims.email ? String(claims.email) : undefined;

  const tokenRoles: string[] = claims?.realm_access?.roles || [];
  const rolesLabel = formatRoles(
    reduxRoles?.length ? reduxRoles : tokenRoles
  );

  return { primary: String(primary), secondary, rolesLabel };
}

const DICTIONARY_LINKS = [
  { title: "Гражданство", path: "/dictionaries/citizenship" },
  { title: "ОП / GOP", path: "/dictionaries/gop" },
  { title: "Степень", path: "/dictionaries/degree" },
  { title: "ВУЗы", path: "/dictionaries/university" },
];

const drawerWidth = 220;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1.5),
  minHeight: 64,
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
    },
  }),
}));

const navItemSx = (open: boolean) => ({
  minHeight: 36,
  py: 0.5,
  justifyContent: open ? "initial" : "center",
  px: open ? 1.5 : 1.25,
  mx: open ? 1 : 0.75,
  my: 0,
  borderRadius: 1,
  "&.Mui-selected": {
    backgroundColor: "rgba(25, 40, 109, 0.08)",
    color: brand.navy,
    "&:hover": {
      backgroundColor: "rgba(25, 40, 109, 0.12)",
    },
  },
});

const navIconSx = (open: boolean) => ({
  minWidth: 0,
  mr: open ? 1.5 : "auto",
  justifyContent: "center",
  color: "inherit",
});

const NavGroup: React.FC<{
  open: boolean;
  label: string;
  visible: boolean;
  isFirst?: boolean;
  children: React.ReactNode;
}> = ({ open, label, visible, isFirst = false, children }) => {
  if (!visible) return null;
  return (
    <>
      {open ? (
        <ListSubheader
          disableSticky
          sx={{
            bgcolor: "transparent",
            lineHeight: 1.2,
            height: "auto",
            py: 0,
            pt: isFirst ? 0.5 : 1,
            pb: 0.25,
            px: 2.25,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: brand.ink,
            opacity: 0.55,
            fontFamily: "inherit",
            userSelect: "none",
          }}
        >
          {label}
        </ListSubheader>
      ) : !isFirst ? (
        <Divider sx={{ my: 0.5, mx: 1.25 }} />
      ) : null}
      {children}
    </>
  );
};

export default function MiniDrawer() {
  const roles = useAppSelector((state) => state.authentication.roles);
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [dictionariesOpen, setDictionariesOpen] = React.useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.pathname.startsWith("/dictionaries")) {
      setDictionariesOpen(true);
    }
  }, [location.pathname]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  function logout(): void {
    clearReturnUrl();
    localStorage.clear();
    navigate("/login");
  }

    const isNotAccountant = () => {
        return !roles || !roles.includes("ROLE_ACCOUNTANT");
    };
    const isNotAudit = () => {
        return !roles || !roles.includes("ROLE_AUDIT");
    };
    const isNotAccountantAndAudit = () => {
        return !roles || !roles.includes("ROLE_ACCOUNTANT") && !roles.includes("ROLE_AUDIT");
    };

  const hasRole = (role) => {
    return roles && roles.includes(role);
  };

  const notAudit = isNotAudit();
  const notAccountantAndAudit = isNotAccountantAndAudit();
  const isAccountant = hasRole("ROLE_ACCOUNTANT");
  const isSystem = hasRole("ROLE_SYSTEM");
  const readOnlyDirector = isReadOnlyDirector(roles);
  const canAssignUniversityManagers =
    hasRole("ROLE_DEPARTMENT_HEAD") ||
    hasRole("ROLE_DEPUTY_DIRECTOR") ||
    hasRole("ROLE_SYSTEM");
  const showStudentsGroup = notAudit || isAccountant;
  const showUniversitiesGroup =
    notAccountantAndAudit || notAudit || canAssignUniversityManagers;
  const showScholarshipGroup = notAudit;

  const currentUser = useMemo(() => getCurrentUserLabel(roles), [roles]);
  const showEsobesNav = ESOBES_ENABLED && (isSystem || isAccountant) && !readOnlyDirector;
  const esobesNav = (
    <ListItem key="Е-Собес" disablePadding sx={{ display: "block" }}>
      <ListItemButton
        selected={location.pathname.startsWith("/esobes")}
        sx={navItemSx(open)}
        onClick={() => navigate("/esobes")}
      >
        <ListItemIcon sx={navIconSx(open)}>
          <SendIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Е-Собес"
          primaryTypographyProps={{ fontSize: 14, noWrap: true }}
          sx={{ opacity: open ? 1 : 0 }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} color="inherit" elevation={0}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minHeight: 64,
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ ...(open && { display: "none" }) }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src="/logo-ru.svg"
              alt="Қаржы орталығы"
              sx={{
                height: 36,
                width: "auto",
                maxWidth: 180,
                objectFit: "contain",
                display: "block",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: 36,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: "18px",
                  color: brand.navy,
                  letterSpacing: 0.2,
                }}
              >
                ГЗС
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "18px",
                  color: brand.muted,
                }}
              >
                Стипендии
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            {currentUser && (
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: { xs: 140, sm: 280 },
                  }}
                >
                  {currentUser.primary}
                </Typography>
                {(currentUser.rolesLabel || currentUser.secondary) && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: { xs: 140, sm: 280 },
                    }}
                  >
                    {[currentUser.rolesLabel, currentUser.secondary]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                )}
              </Box>
            )}
            <Tooltip title="Выход">
              <IconButton
                edge="end"
                onClick={() => setLogoutConfirmOpen(true)}
                aria-label="logout"
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ justifyContent: open ? "space-between" : "center" }}>
          {open && (
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.secondary",
                letterSpacing: 0.2,
                pl: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Меню
            </Box>
          )}
          <IconButton
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            size="small"
            aria-label={open ? "close drawer" : "open drawer"}
          >
            {open ? (
              theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )
            ) : (
              <MenuIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List dense sx={{ px: 0, py: 0.5 }}>
          <NavGroup open={open} label="Обзор" visible isFirst>
            <ListItem key={"Дашборд"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/home")}
                sx={navItemSx(open)}
                onClick={() => navigate("/home")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <AnalyticsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Дашборд"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          </NavGroup>

          <NavGroup open={open} label="Студенты" visible={showStudentsGroup}>
            {notAudit && (
              <ListItem
                key={"Студенты"}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  selected={
                    location.pathname === "/student" ||
                    location.pathname.startsWith("/student/")
                  }
                  sx={navItemSx(open)}
                  onClick={() => navigate("/student")}
                >
                  <ListItemIcon sx={navIconSx(open)}>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Студенты"}
                    primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {isAccountant && (
              <ListItem
                key={"Справка о стипендии"}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  selected={location.pathname.startsWith(
                    "/student-scholarship"
                  )}
                  sx={navItemSx(open)}
                  onClick={() => navigate("/student-scholarship")}
                >
                  <ListItemIcon sx={navIconSx(open)}>
                    <NewspaperIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Справка о стипендии"}
                    primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </NavGroup>

          <NavGroup open={open} label="ВУЗы" visible={showUniversitiesGroup}>
            {notAudit && (
              <ListItem
                key={"Отчеты ВУЗов"}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  selected={location.pathname.startsWith("/report")}
                  sx={navItemSx(open)}
                  onClick={() => navigate("/report")}
                >
                  <ListItemIcon sx={navIconSx(open)}>
                    <ArticleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Отчеты ВУЗов"}
                    primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {notAccountantAndAudit && (
              <ListItem key={"Вузы"} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  selected={
                    location.pathname === "/university" ||
                    location.pathname.startsWith("/university/")
                  }
                  sx={navItemSx(open)}
                  onClick={() => navigate("/university")}
                >
                  <ListItemIcon sx={navIconSx(open)}>
                    <SchoolIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Вузы"}
                    primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {canAssignUniversityManagers && (
              <ListItem
                key={"Назначение "}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  selected={location.pathname.startsWith(
                    "/university-responsible"
                  )}
                  sx={navItemSx(open)}
                  onClick={() => navigate("/university-responsible")}
                >
                  <ListItemIcon sx={navIconSx(open)}>
                    <ManageAccountsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Назначение менеджеров ОВПО"}
                    primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </NavGroup>

          <NavGroup
            open={open}
            label="Стипендия"
            visible={showScholarshipGroup}
          >
            <ListItem
              key={"Калькулятор стипендии"}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                selected={location.pathname.startsWith(
                  "/scholarship-calculator"
                )}
                sx={navItemSx(open)}
                onClick={() => navigate("/scholarship-calculator")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <CalculateIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Калькулятор стипендии"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          </NavGroup>

          <NavGroup open={open} label="Система" visible>
            <ListItem
              key={"Справочники"}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                selected={location.pathname.startsWith("/dictionaries")}
                sx={navItemSx(open)}
                onClick={() => {
                  if (!open) {
                    setOpen(true);
                    setDictionariesOpen(true);
                    return;
                  }
                  setDictionariesOpen((value) => !value);
                }}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <MenuBookIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Справочники"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
                {open ? (
                  dictionariesOpen ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )
                ) : null}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && dictionariesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {DICTIONARY_LINKS.map((item) => (
                  <ListItem key={item.path} disablePadding sx={{ display: "block" }}>
                    <ListItemButton
                      selected={
                        location.pathname === item.path ||
                        location.pathname.startsWith(`${item.path}/`)
                      }
                      sx={{ ...navItemSx(open), pl: 4.5 }}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
            {notAccountantAndAudit && (
              <>
                {!readOnlyDirector && (
                <ListItem
                  key={"Логи ШЭП"}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    selected={location.pathname.startsWith("/shep-logs")}
                    sx={navItemSx(open)}
                    onClick={() => navigate("/shep-logs")}
                  >
                    <ListItemIcon sx={navIconSx(open)}>
                      <SyncAltIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={"Логи ШЭП"}
                      primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
                )}
                <ListItem
                  key={"Почта"}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    selected={location.pathname.startsWith("/message")}
                    sx={navItemSx(open)}
                    onClick={() => navigate("/message")}
                  >
                    <ListItemIcon sx={navIconSx(open)}>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={"Почта"}
                      primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </NavGroup>

          <NavGroup open={open} label="Интеграции" visible={isSystem || isAccountant}>
            <ListItem
              key={"Сведения о студенте (ЕПВО)"}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                selected={location.pathname.startsWith("/epvo-student")}
                sx={navItemSx(open)}
                onClick={() => navigate("/epvo-student")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <BadgeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Сведения о студенте (ЕПВО)"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            {isSystem && (
            <>
            <ListItem key={"КДП"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/kdp")}
                sx={navItemSx(open)}
                onClick={() => navigate("/kdp")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <VerifiedUserIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"КДП"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem key={"ГБД ФЛ"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/gbdfl")}
                sx={navItemSx(open)}
                onClick={() => navigate("/gbdfl")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <ContactPageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"ГБД ФЛ"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem key={"ГБД ЮЛ"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/gbdul")}
                sx={navItemSx(open)}
                onClick={() => navigate("/gbdul")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"ГБД ЮЛ"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem key={"Безработные (МТСЗН)"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/unemployed")}
                sx={navItemSx(open)}
                onClick={() => navigate("/unemployed")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <WorkOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Безработные (МТСЗН)"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            {isSystem && showEsobesNav && esobesNav}
            <ListItem key={"НОБД"} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                selected={location.pathname.startsWith("/nobd")}
                sx={navItemSx(open)}
                onClick={() => navigate("/nobd")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <ClassIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"НОБД"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              key={"Логи интеграций"}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                selected={location.pathname.startsWith("/integration-logs")}
                sx={navItemSx(open)}
                onClick={() => navigate("/integration-logs")}
              >
                <ListItemIcon sx={navIconSx(open)}>
                  <SyncAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={"Логи интеграций"}
                  primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
            </>
            )}
            {!isSystem && showEsobesNav && esobesNav}
          </NavGroup>
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minWidth: 0,
          height: "100%",
          overflow: "auto",
          bgcolor: "background.paper",
        }}
      >
        <DrawerHeader />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          <AppRoutes />
        </Box>
      </Box>
      <Dialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Выход</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите выйти?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutConfirmOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLogoutConfirmOpen(false);
              logout();
            }}
          >
            Выйти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
